import { useFrame } from "@react-three/fiber";
import { type ReactNode, useRef } from "react";
import { Box3, type Object3D } from "three";

interface ProductFloorAlignerProps {
  children: ReactNode;
  floorY?: number;
  scale?: number;
}

const THRESHOLD = 0.0001;
const _tempBox = new Box3();

/**
 * Expands the target box with all geometry in the subtree,
 * skipping measurement overlays and drag helpers so only the
 * actual product geometry determines the floor position.
 */
function expandBoxFromProduct(obj: Object3D, target: Box3): void {
  if (obj.userData?.isMeasurementBox || obj.userData?.isDragHelper) return;

  // biome-ignore lint/suspicious/noExplicitAny: accessing .geometry which exists on Mesh but not on Object3D
  const mesh = obj as any;
  if (mesh.geometry) {
    if (!mesh.geometry.boundingBox) {
      mesh.geometry.computeBoundingBox();
    }
    _tempBox.copy(mesh.geometry.boundingBox);
    _tempBox.applyMatrix4(mesh.matrixWorld);
    target.union(_tempBox);
  }

  for (const child of obj.children) {
    expandBoxFromProduct(child, target);
  }
}

/**
 * Continuously monitors the bounding box of product geometry (excluding
 * measurement overlays and drag helpers) and adjusts the group's Y position
 * so the product bottom sits at the specified floor level.
 *
 * Uses localMin (= worldBottom - position.y) which is invariant to our own
 * position changes, so this cannot cause an infinite loop.
 */
export const ProductFloorAligner = ({
  children,
  floorY = 0,
  scale = 1 / 1000,
}: ProductFloorAlignerProps) => {
  const groupRef = useRef<Object3D>(null);
  const lastLocalMinRef = useRef<number | null>(null);
  const boxRef = useRef(new Box3());

  useFrame(() => {
    if (!groupRef.current) return;

    // Force-update all descendant world matrices so newly mounted
    // meshes have correct transforms before bounding box computation
    groupRef.current.updateMatrixWorld(true);

    boxRef.current.makeEmpty();
    expandBoxFromProduct(groupRef.current, boxRef.current);
    if (boxRef.current.isEmpty()) return;

    const worldBottom = boxRef.current.min.y;
    const currentY = groupRef.current.position.y;
    const localMin = worldBottom - currentY;

    if (
      lastLocalMinRef.current === null ||
      Math.abs(localMin - lastLocalMinRef.current) > THRESHOLD
    ) {
      lastLocalMinRef.current = localMin;
      groupRef.current.position.y = floorY - localMin;
    }
  });

  return (
    <group ref={groupRef} scale={scale}>
      {children}
    </group>
  );
};
